/**
 * Trips Repository - Data access layer for trips
 */

import api from "../../../shared/api.jsx";

const TRIPS_ENDPOINTS = {
  base: "/trips",
  byId: (id) => `/trips/${id}`,
  status: (id) => `/trips/${id}/status`,
  orders: (id) => `/trips/${id}/orders`,
  order: (id, orderId) => `/trips/${id}/orders/${orderId}`,
  risks: (id) => `/trips/${id}/risks`,
  summary: (id) => `/trips/${id}/summary`,
};

/**
 * Get all trips with optional filters
 * @param {Object} filters - Filter options
 * @param {string} [filters.status] - Trip status filter
 * @param {string[]} [filters.statusIn] - Active status values to include
 * @param {string} [filters.routeCorridor] - Route corridor filter
 * @param {string} [filters.departureDate] - Departure date filter
 * @param {string} [filters.vehicleId] - Vehicle ID filter
 * @param {string} [filters.driverId] - Driver ID filter
 * @param {number} [page=1] - Page number
 * @param {number} [limit=20] - Items per page
 * @returns {Promise<Object>} API response
 */
export const fetchTrips = async (filters = {}, page = 1, limit = 20) => {
  const params = new URLSearchParams();

  if (filters.statusIn?.length) {
    params.append("statusIn", filters.statusIn.join(","));
  } else if (filters.status) {
    params.append("status", filters.status);
  }
  if (filters.routeCorridor) params.append("routeCorridor", filters.routeCorridor);
  if (filters.departureDate) params.append("departureDate", filters.departureDate);
  if (filters.vehicleId) params.append("vehicleId", filters.vehicleId);
  if (filters.driverId) params.append("driverId", filters.driverId);
  if (filters.pastDue !== undefined && filters.pastDue !== null) {
    params.append("pastDue", String(filters.pastDue));
  }
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const url = `${TRIPS_ENDPOINTS.base}?${params}`;
  const response = await api.get(url);
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Get a single trip by ID
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Object>} API response
 */
export const fetchTripById = async (tripId) => {
  const response = await api.get(TRIPS_ENDPOINTS.byId(tripId));
  // API client returns JSON directly, check if it has success property
  if (response && response.success !== undefined) {
    return {
      success: response.success,
      data: response.data || response,
    };
  }
  // Fallback if response is already the data
  return {
    success: true,
    data: response.data || response,
  };
};

/**
 * Create a new trip
 * @param {Object} tripData - Trip data
 * @returns {Promise<Object>} API response
 */
export const createTrip = async (tripData) => {
  const response = await api.post(TRIPS_ENDPOINTS.base, tripData);
  return response;
};

/**
 * Update an existing trip
 * @param {string} tripId - Trip identifier
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} API response
 */
export const updateTrip = async (tripId, updateData) => {
  const response = await api.put(TRIPS_ENDPOINTS.byId(tripId), updateData);
  return response;
};

/**
 * Update trip status
 * @param {string} tripId - Trip identifier
 * @param {string} status - New status
 * @returns {Promise<Object>} API response
 */
export const updateTripStatus = async (tripId, status) => {
  const response = await api.patch(TRIPS_ENDPOINTS.status(tripId), {
    status,
  });
  return response;
};

/**
 * Add order to trip
 * @param {string} tripId - Trip identifier
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const addOrderToTrip = async (tripId, orderId) => {
  const response = await api.post(TRIPS_ENDPOINTS.orders(tripId), {
    orderId,
  });
  return response;
};

/**
 * Remove order from trip
 * @param {string} tripId - Trip identifier
 * @param {string} orderId - Order identifier
 * @returns {Promise<Object>} API response
 */
export const removeOrderFromTrip = async (tripId, orderId) => {
  const response = await api.delete(TRIPS_ENDPOINTS.order(tripId, orderId));
  return response;
};

/**
 * Delete a trip
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Object>} API response
 */
export const deleteTrip = async (tripId) => {
  const response = await api.delete(TRIPS_ENDPOINTS.byId(tripId));
  return response;
};

/**
 * Get risk signals for a trip
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Object>} API response with risk signals
 */
export const fetchTripRisks = async (tripId) => {
  const response = await api.get(TRIPS_ENDPOINTS.risks(tripId));
  return {
    success: true,
    data: response.data,
  };
};

/**
 * Get aggregate trip summary (capacity, revenue, risks).
 * @param {string} tripId
 * @returns {Promise<Object>}
 */
export const fetchTripSummary = async (tripId) => {
  const response = await api.get(TRIPS_ENDPOINTS.summary(tripId));
  if (response && response.success !== undefined) {
    return {
      success: response.success,
      data: response.data || response,
    };
  }
  return {
    success: true,
    data: response.data || response,
  };
};

